/**
 * @file AgentLayout.jsx
 * @description AgentLayout component for displaying and managing agent layout.
 * @component AgentLayout
 * @exports AgentLayout - Main component for displaying and managing agent layout.
 */

import React, { useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const AgentLayout = ({
  leftPanelTitle,
  leftPanel,
  tabConfig,
  actions,
  menuActions,
  defaultTab = "",
  title = "",
}) => {
  const [currentTab, setCurrentTab] = useState(defaultTab);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderTabContent = () => {
    const current = tabConfig.find((tab) => tab.value === currentTab);
    return current?.component || null;
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh"}}>
      {/* Left Panel */}
      <Box
        sx={{
          minWidth: 375,
          p: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #fefefe 0%, #f9f9fb 100%)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s ease",
          minHeight: "100vh", // Full height on initial load
          height: "auto", // Auto height as list grows
          "&:hover": {
            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-2px)",
          },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
          }}
        >
          <Typography gutterBottom>{leftPanelTitle}</Typography>
        </Box>
        {leftPanel}
      </Box>

      {/* Right Panel */}
      <Box sx={{ flexGrow: 1, p: [0, 2] }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title && <Typography>{title}</Typography>}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {actions && <Box sx={{ mr: 1 }}>{actions}</Box>}
            {menuActions && (
              <Box sx={{ mr: 1 }}>
                {" "}
                <IconButton onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {Array.isArray(menuActions)
                    ? menuActions.map((action, idx) => (
                        <MenuItem
                          sx={{ cursor: "pointer", fontSize: "14px" }}
                          key={idx}
                          onClick={
                            action.component
                              ? undefined
                              : () => {
                                  handleMenuClose();
                                  action.onClick && action.onClick();
                                }
                          }
                        >
                          {action.component ? action.component : action.label}
                        </MenuItem>
                      ))
                    : null}
                </Menu>
              </Box>
            )}
          </Box>
        </Box>

        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Tabs for content"
        >
          {tabConfig.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
        <Box mt={2}>{renderTabContent()}</Box>
      </Box>
    </Box>
  );
};

export default AgentLayout;
